import { useEffect, useState, useRef, useCallback } from "react";
import { StyleSheet, TextInput, TextInputProps, StyleProp, ViewStyle, Platform, View } from "react-native";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";
import { Button } from "./ui/Button";

type Props = TextInputProps & {
  style?: StyleProp<ViewStyle>;
  keyWords?: string[];
  value: string;
  setValue?: (v: string) => void;
  textRef?: React.LegacyRef<TextInput> | undefined;
  showClearButton?: boolean;
  debounceMs?: number;
};

export default function Search({
  style,
  textRef,
  value,
  setValue,
  onFocus,
  onBlur,
  showClearButton = false,
  debounceMs = 300,
  ...rest
}: Props) {
  const { colors } = useTheme();

  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(value);

  // Track the timeout and latest callback in refs to avoid stale closures
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setValueRef = useRef(setValue);
  setValueRef.current = setValue;

  // Track if we're currently typing to avoid external value overwriting input
  const isTypingRef = useRef(false);

  // Sync external value changes only when not actively typing
  useEffect(() => {
    if (!isTypingRef.current && value !== localValue) {
      setLocalValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle text changes with proper debouncing
  const handleChangeText = useCallback(
    (text: string) => {
      // Update local state immediately for responsive UI
      setLocalValue(text);
      isTypingRef.current = true;

      // Clear any pending debounce
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Schedule debounced callback
      timeoutRef.current = setTimeout(() => {
        setValueRef.current?.(text);
        isTypingRef.current = false;
      }, debounceMs);
    },
    [debounceMs],
  );

  const handleFocus = (e: any) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: any) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  // Clear handler that immediately updates both local and parent state
  const handleClear = useCallback(() => {
    setLocalValue("");
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setValueRef.current?.("");
    isTypingRef.current = false;
  }, []);

  const borderColor = isFocused ? colors.primary : colors.border;

  return (
    <View style={[styles.container, { borderColor, backgroundColor: colors.background }, style]}>
      {localValue !== "" && showClearButton && (
        <Button style={styles.button} icon={"close"} iconSize={14} onPress={handleClear} />
      )}
      <TextInput
        value={localValue}
        onChangeText={handleChangeText}
        ref={textRef}
        autoFocus={false}
        autoComplete="off"
        style={[styles.input, { color: colors.text }]}
        placeholderTextColor={colors.placeholder}
        placeholder={"Search"}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...rest}
      />
      <MaterialIcons
        name={"search"}
        size={20}
        color={isFocused ? colors.primary : colors.placeholder}
        style={styles.icon}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 6,
    paddingHorizontal: 8,
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
    verticalAlign: "middle",
  },
  input: {
    width: "100%",
    fontSize: 16,
    height: 28,
    borderWidth: 0,
    borderColor: "transparent",
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  button: {
    left: -4,
    borderRadius: 999,
    maxWidth: 14,
    maxHeight: 14,
    minHeight: 22,
    minWidth: 22,
  },
  icon: {
    marginHorizontal: 2,
    marginVertical: "auto",
  },
});
